import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { DeckCardKakaoShareInput, getDeckCardKakaoI18nKeys, isKakaoShareAvailable, shareDeckCardToKakao } from "@/modules/kakao";

export const useKakaoDeckCardShare = () => {
  const { t } = useTranslation();
  const [isSharing, setIsSharing] = useState(false);
  const isConfigured = isKakaoShareAvailable();

  const shareDeckCard = useCallback(
    async (input: DeckCardKakaoShareInput) => {
      if (!isConfigured) {
        enqueueSnackbar(
          t("kakao_share_not_available", {
            ns: "common",
            defaultValue: "KakaoTalk sharing is not configured.",
          }),
          { variant: "warning" },
        );
        return false;
      }

      const keys = getDeckCardKakaoI18nKeys(input.kind);

      setIsSharing(true);
      try {
        await shareDeckCardToKakao(input, {
          cardTitle: t(keys.kakaoCardTitle, {
            ns: "taascard",
            title: input.cardTitle ?? input.talentName,
            defaultValue: "{{title}}",
          }),
          senderLine: t(keys.kakaoSenderLine, {
            ns: "taascard",
            sender: input.senderName || input.talentName,
            defaultValue: "{{sender}} shared a card",
          }),
          shortHint: t(keys.kakaoShortHint, {
            ns: "taascard",
            talent: input.talentName,
            defaultValue: "Explore this card on TaaSNet.",
          }),
          buttonTitle: t(keys.kakaoOpenButton, {
            ns: "taascard",
            defaultValue: "카드 열기",
          }),
        });
        return true;
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : t("kakao_share_failed", {
                ns: "common",
                defaultValue: "Could not open KakaoTalk share.",
              });
        enqueueSnackbar(message, { variant: "error" });
        return false;
      } finally {
        setIsSharing(false);
      }
    },
    [isConfigured, t],
  );

  return {
    shareDeckCard,
    isSharing,
    isConfigured,
  };
};
